import { createPhotoCollection } from './listing-photo-collection';
import { createListingPhotoLightbox } from './listing-photo-lightbox';

/** Owns photo order and rendering; persistence is injected by the form adapter. */
export function createListingPhotoEditor({ list, modal, onRemove, onChange }) {
    const collection = createPhotoCollection();
    const lightbox = createListingPhotoLightbox(modal, collection.getItems);
    let busy = false, dragging = '';
    function button(text, label, hook, action) {
        const node = document.createElement('button');
        node.type = 'button'; node.className = 'ms-button ms-button--secondary ms-button--sm';
        node.textContent = text; node.setAttribute('aria-label', label); node.setAttribute(hook, '');
        node.disabled = busy;
        node.addEventListener('click', action);
        return node;
    }
    function changed(key, hook) {
        render();
        onChange(collection.getItems());
        const figures = [...list.children];
        figures.find(figure => figure.dataset.photoKey === key)?.querySelector(`[${hook}]:not(:disabled)`)?.focus();
    }
    function clearDrag() {
        dragging = '';
        [...list.children].forEach(node => node.classList.remove('is-dragging', 'is-drop-before', 'is-drop-after'));
    }
    function render() {
        list.replaceChildren();
        const photos = collection.getItems();
        photos.forEach((photo, index) => {
            const figure = document.createElement('figure');
            figure.className = 'plf-photo'; figure.dataset.photoKey = photo.key;
            const caption = document.createElement('figcaption');
            caption.setAttribute('data-plf-photo-caption', '');
            caption.textContent = `${index === 0 ? 'Zdjęcie główne' : `Zdjęcie ${index + 1}`} · ${photo.label || ''}`;
            const remove = button('Usuń', `Usuń zdjęcie ${index + 1}`, 'data-plf-photo-remove', () => { if (!busy) onRemove(photo); });
            const zoom = button('⛶', `Powiększ zdjęcie ${index + 1}`, 'data-plf-photo-zoom', () => lightbox.open(photo.key, zoom));
            zoom.classList.add('plf-photo__zoom'); zoom.disabled = busy || !photo.url;
            if (photo.url) {
                const image = document.createElement('img');
                image.src = photo.url; image.alt = `Zdjęcie ${index + 1}`; image.draggable = false;
                image.addEventListener('error', () => {
                    image.hidden = true; zoom.disabled = true;
                    caption.textContent = 'Nie można wyświetlić zdjęcia. Usuń je i dodaj plik JPEG lub WebP.';
                });
                figure.append(image);
            } else {
                const processing = document.createElement('div');
                processing.className = 'plf-photo__processing'; processing.textContent = 'Przetwarzanie zdjęcia'; figure.append(processing);
            }
            const controls = document.createElement('div'); controls.className = 'plf-photo__order';
            for (const [offset, text, hook] of [[-1, '←', 'data-plf-photo-previous'], [1, '→', 'data-plf-photo-next']]) {
                const move = button(text, `Przesuń zdjęcie ${index + 1} ${offset < 0 ? 'wcześniej' : 'później'}`, hook, () => {
                    if (!busy && collection.move(photo.key, offset)) changed(photo.key, offset < 0 ? 'data-plf-photo-next' : 'data-plf-photo-previous');
                });
                move.disabled = busy || index + offset < 0 || index + offset >= photos.length;
                controls.append(move);
            }
            const handle = document.createElement('span'); handle.className = 'plf-photo__drag-handle';
            handle.setAttribute('data-plf-photo-drag-handle', ''); handle.setAttribute('aria-hidden', 'true');
            handle.textContent = '⠿'; handle.title = 'Przeciągnij, aby zmienić kolejność zdjęć'; handle.draggable = !busy && photos.length > 1;
            handle.addEventListener('dragstart', event => {
                if (busy) { event.preventDefault(); return; }
                dragging = photo.key; figure.classList.add('is-dragging');
                event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', photo.key);
            });
            figure.addEventListener('dragover', event => {
                if (busy || !dragging || dragging === photo.key) return;
                event.preventDefault();
                const rect = figure.getBoundingClientRect();
                [...list.children].forEach(node => node.classList.remove('is-drop-before', 'is-drop-after'));
                figure.classList.add(event.clientX > rect.left + rect.width / 2 ? 'is-drop-after' : 'is-drop-before');
            });
            figure.addEventListener('drop', event => {
                if (busy || !dragging) return;
                event.preventDefault();
                const rect = figure.getBoundingClientRect();
                if (collection.drop(dragging, photo.key, event.clientX > rect.left + rect.width / 2)) changed(dragging, 'data-plf-photo-remove');
                clearDrag();
            });
            handle.addEventListener('dragend', clearDrag);
            figure.append(caption, handle, controls, remove, zoom); list.append(figure);
        });
    }
    return {
        getItems: collection.getItems,
        setItems(items, preserveOrder = true) { collection.setItems(items, preserveOrder); render(); },
        setBusy(value) { if (busy !== value) { busy = value; render(); } },
    };
}
